/**
 * deliveryStatusApi — the aggregate fleet delivery read (delivery-status
 * capability) behind the Delivery status page, the results-modal summary, and
 * the top-bar indicator.
 *
 * One request answers for every scope; the server is ledger-first (settled
 * scopes from records, full hardened verification only for the active
 * subset). Clients poll ONLY this aggregate — never per-scope fan-out — on
 * the ~one-minute verification cycle, and only while attention is warranted
 * (scopes propagating or a run in progress). All surfaces share one query
 * cache entry per content item.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { fetchWithAuthRetry } from './apiUtils';
import type { PublishableContentKind } from './publishApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/** The client poll cycle while scopes are pending (mirrors the server's cadence). */
export const VERIFY_CYCLE_MS = 60_000;

export type DeliveryScopeState =
  | 'current' | 'propagating' | 'stale' | 'neverDeployed' | 'partial' | 'failed' | 'unknown';

/** Which tier produced a scope's state: recorded outcome, hosted stamp, or the full served check. */
export type DeliveryScopeSource = 'ledger' | 'origin' | 'verified';

export interface DeliveryLessonCounts {
  total: number;
  stale: number;
  missing: number;
}

export interface DeliveryScopeEntry {
  accountSlug: string;
  externalAccountId: number | null;
  base: boolean;
  hasCustomizations: boolean;
  state: DeliveryScopeState;
  source: DeliveryScopeSource;
  reason?: string;
  invalidation?: { state: string };
  deployId?: string;
  deployedAt?: string | null;
  expectedLiveAt?: string;
  /** The scope's delivery expectation — max(base publish, account publish). Feeds the per-row fast check. */
  expectedPublishedAt?: string | null;
  lessons?: DeliveryLessonCounts;
  ledger?: { success: boolean; error?: string; warning?: string; recordedAt: string };
}

export type DeliveryRunState = 'running' | 'complete' | 'complete-with-failures' | 'stalled';

export interface DeliveryRunSummary {
  runId: string;
  startedAt: string;
  initiatedByUserId: number | null;
  initiatedAccountId: number | null;
  intendedScopeCount: number;
  recordedScopeCount: number;
  failedScopeCount: number;
  state: DeliveryRunState;
  lastActivityAt: string;
  /** Run-level failure (died before targeting) — no scope outcome exists for it. */
  error?: string | null;
}

export interface DeliveryStatusSnapshot {
  checkedAt: string;
  publish: { lastPublishedAt: string | null };
  summary: {
    total: number;
    current: number;
    propagating: number;
    stale: number;
    neverDeployed: number;
    partial: number;
    failed: number;
    unknown: number;
  };
  scopes: DeliveryScopeEntry[];
  run?: DeliveryRunSummary;
}

export class DeliveryStatusError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'DeliveryStatusError';
  }
}

export async function fetchDeliveryStatus(
  contentKind: PublishableContentKind,
  packageId: string,
  /** Pins the run this client just executed — the server bypasses any cached
   * snapshot (its own or another instance's TTL notwithstanding) that does
   * not acknowledge it. */
  expectedRunId?: string,
): Promise<DeliveryStatusSnapshot> {
  const expectedQuery = expectedRunId ? `&expectedRunId=${encodeURIComponent(expectedRunId)}` : '';
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/courses/${packageId}/delivery-status?kind=${contentKind}${expectedQuery}`,
  );
  if (!response.ok) {
    let message = `Delivery status failed (${response.status})`;
    try {
      const body = (await response.json()) as { message?: unknown };
      if (body && typeof body.message === 'string') message = body.message;
    } catch {
      // Non-JSON error body; keep the status-based message.
    }
    throw new DeliveryStatusError(message, response.status);
  }
  return (await response.json()) as DeliveryStatusSnapshot;
}

/**
 * Kind is part of the identity: the slugs are codec-ambiguous, so a course
 * and a lesson can share the same slug string — they must never share a
 * cached fleet.
 */
export const deliveryStatusQueryKey = (contentKind: PublishableContentKind, packageId: string) =>
  ['deliveryStatus', contentKind, packageId] as const;

/** True while the fleet warrants automatic rechecks. */
export const deliveryStatusPending = (snapshot: DeliveryStatusSnapshot | undefined): boolean =>
  snapshot !== undefined
  && (snapshot.summary.propagating > 0 || snapshot.run?.state === 'running');

/**
 * Bounded post-publish freshness loop. Each attempt pins `expectedRunId`, so
 * the SERVER bypasses any cached snapshot — its own or another instance's,
 * regardless of TTL — that does not acknowledge the run; the loop only
 * absorbs write/read propagation, not cache lifetimes. Without this, a
 * client could cache a pre-publish "settled" fleet and stop polling.
 */
export async function pollDeliveryUntilRun(
  queryClient: QueryClient,
  contentKind: PublishableContentKind,
  packageId: string,
  runId: string,
  options?: { attempts?: number; delayMs?: number; signal?: AbortSignal },
): Promise<void> {
  const attempts = options?.attempts ?? 5;
  const delayMs = options?.delayMs ?? 3_000;
  const signal = options?.signal;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (signal?.aborted) return;
    try {
      const snapshot = await queryClient.fetchQuery({
        queryKey: deliveryStatusQueryKey(contentKind, packageId),
        queryFn: () => fetchDeliveryStatus(contentKind, packageId, runId),
        staleTime: 0,
      });
      if (snapshot.run?.runId === runId) return;
    } catch {
      // Transient — the retry below is the point.
    }
    // No trailing wait after the final attempt, and an abort (surface
    // unmounted / user navigated away) releases the delay immediately.
    if (attempt === attempts - 1 || signal?.aborted) return;
    await new Promise<void>((resolve) => {
      const onAbort = () => { clearTimeout(timer); resolve(); };
      const timer = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve(); }, delayMs);
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  }
}

/**
 * The one poll stream per viewer: modal summary, top-bar indicator, and the
 * Delivery status page all read this key. Polling runs at the verification
 * cycle only while pending, stops when the fleet settles, and pauses while
 * the tab is hidden (`refetchIntervalInBackground: false`).
 */
export function useDeliveryStatus(
  contentKind: PublishableContentKind,
  packageId: string | null,
  enabled = true,
  poll = true,
) {
  return useQuery({
    queryKey: deliveryStatusQueryKey(contentKind, packageId ?? ''),
    queryFn: () => fetchDeliveryStatus(contentKind, packageId ?? ''),
    enabled: enabled && Boolean(packageId),
    // ONE poll stream per viewer (spec): every observer shares the query key,
    // but each polling observer owns an interval timer — phase-shifted timers
    // would multiply the cadence. Non-owner surfaces (summary lines, modal)
    // subscribe with poll=false and ride the owner's refreshes.
    refetchInterval: (query) =>
      (poll && deliveryStatusPending(query.state.data) ? VERIFY_CYCLE_MS : false),
    refetchIntervalInBackground: false,
    // The server snapshot is itself cached just under the cycle; client-side
    // staleness shorter than that only causes double-fetch churn.
    staleTime: 30_000,
    retry: 1,
    // The app-wide default keeps previous data across key changes — for this
    // query that would render the PREVIOUS content's fleet while navigating.
    placeholderData: undefined,
  });
}
